import { useState, useRef, useCallback } from 'react';

/**
 * 列表拖拽排序 Hook
 */
export const useDraggableList = <T extends unknown>(
  initialItems: T[],
  onOrderChange?: (newItems: T[]) => void
) => {
  const [items, setItems] = useState(initialItems);
  const draggingItemRef = useRef<number | null>(null);
  const dragOverItemRef = useRef<number | null>(null);

  const onDragStart = useCallback((index: number) => {
    draggingItemRef.current = index;
  }, []);

  const onDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    dragOverItemRef.current = index;
  }, []);

  const onDrop = useCallback(() => {
    const draggedIndex = draggingItemRef.current;
    const dragOverIndex = dragOverItemRef.current;

    if (draggedIndex === null || dragOverIndex === null || draggedIndex === dragOverIndex) {
      draggingItemRef.current = null;
      dragOverItemRef.current = null;
      return;
    }

    const newItems = [...items];
    const [draggedItem] = newItems.splice(draggedIndex, 1);
    newItems.splice(dragOverIndex, 0, draggedItem);

    setItems(newItems);
    draggingItemRef.current = null;
    dragOverItemRef.current = null;

    // 触发排序变化回调
    onOrderChange?.(newItems);
  }, [items, onOrderChange]);

  const onDragEnd = useCallback(() => {
    draggingItemRef.current = null;
    dragOverItemRef.current = null;
  }, []);

  return {
    items,
    setItems,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    draggingIndex: draggingItemRef.current,
    dragOverIndex: dragOverItemRef.current,
  };
};
